import { useEffect, useState } from "react";
import { api } from "../api";
import type { Schedule, ScheduleInput, Tier, Weekday } from "../../server/types";

const WEEKDAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function emptyInput(): ScheduleInput {
  return { time_of_day: "03:00:00", days_of_week: null, days_of_month: null, tier: "daily", enabled: true };
}

function describe(s: Schedule): string {
  const parts = [`${s.time_of_day} local`];
  if (s.days_of_week) parts.push(`on ${s.days_of_week.join(", ")}`);
  if (s.days_of_month) parts.push(`day ${s.days_of_month.join(", ")} of month`);
  if (!s.days_of_week && !s.days_of_month) parts.push("every day");
  return parts.join(" · ");
}

export default function SchedulesPage() {
  const [items, setItems] = useState<Schedule[]>([]);
  const [editing, setEditing] = useState<{ id?: number; data: ScheduleInput } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try { setItems((await api.getSchedules()).schedules); }
    catch (e) { setErr((e as Error).message); }
  }
  useEffect(() => { void refresh(); }, []);

  async function save() {
    if (!editing) return;
    setErr(null);
    try {
      if (editing.id) await api.updateSchedule(editing.id, editing.data);
      else await api.createSchedule(editing.data);
      setEditing(null);
      void refresh();
    } catch (e) { setErr((e as Error).message); }
  }

  async function remove(id: number) {
    if (!confirm("Delete this schedule?")) return;
    try { await api.deleteSchedule(id); void refresh(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <>
      <h2>Schedules</h2>
      {err && <div className="error-box" style={{ marginBottom: 16 }}>{err}</div>}

      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="muted">{items.length} schedule(s) configured</span>
          <button className="primary" onClick={() => setEditing({ data: emptyInput() })}>
            + New schedule
          </button>
        </div>
      </div>

      {editing && (
        <div className="card">
          <h3>{editing.id ? "Edit schedule" : "New schedule"}</h3>

          <div className="grid-2">
            <div>
              <label>Time of day (HH:MM:SS, local)</label>
              <input value={editing.data.time_of_day} onChange={(e) =>
                setEditing({ ...editing, data: { ...editing.data, time_of_day: e.target.value } })} />
            </div>
            <div>
              <label>Tier</label>
              <select value={editing.data.tier} onChange={(e) =>
                setEditing({ ...editing, data: { ...editing.data, tier: e.target.value as Tier } })}>
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="monthly">monthly</option>
              </select>
            </div>
          </div>

          <label style={{ marginTop: 16 }}>Days of week (leave all unchecked = every day)</label>
          <div className="checkbox-row">
            {WEEKDAYS.map((w) => {
              const checked = editing.data.days_of_week?.includes(w) ?? false;
              return (
                <label key={w} className={checked ? "checked" : ""}>
                  <input type="checkbox" checked={checked} onChange={(e) => {
                    const cur = new Set(editing.data.days_of_week ?? []);
                    if (e.target.checked) cur.add(w); else cur.delete(w);
                    const next = cur.size > 0 ? Array.from(cur) : null;
                    setEditing({ ...editing, data: { ...editing.data, days_of_week: next } });
                  }} />
                  {w}
                </label>
              );
            })}
          </div>

          <label style={{ marginTop: 16 }}>Days of month (comma-separated, e.g. <code>1, 15</code>)</label>
          <input
            placeholder="1, 15"
            value={editing.data.days_of_month?.join(", ") ?? ""}
            onChange={(e) => {
              const txt = e.target.value.trim();
              if (!txt) {
                setEditing({ ...editing, data: { ...editing.data, days_of_month: null } });
                return;
              }
              const arr = txt.split(",").map((s) => Number(s.trim())).filter((n) => Number.isInteger(n));
              setEditing({ ...editing, data: { ...editing.data, days_of_month: arr.length ? arr : null } });
            }}
          />

          <label style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" style={{ width: "auto" }} checked={editing.data.enabled}
              onChange={(e) => setEditing({ ...editing, data: { ...editing.data, enabled: e.target.checked } })} />
            <span>Enabled</span>
          </label>

          <div className="row" style={{ marginTop: 16 }}>
            <button className="primary" onClick={save}>Save</button>
            <button onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Time</th><th>Tier</th><th>Filter</th><th>Enabled</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="muted" style={{ padding: 24, textAlign: "center" }}>No schedules yet.</td></tr>}
            {items.map((s) => (
              <tr key={s.id}>
                <td><code>{s.time_of_day}</code></td>
                <td><span className={`badge ${s.tier}`}>{s.tier}</span></td>
                <td>{describe(s)}</td>
                <td>{s.enabled ? "yes" : <span className="muted">no</span>}</td>
                <td style={{ textAlign: "right" }}>
                  <button onClick={() => setEditing({ id: s.id, data: { time_of_day: s.time_of_day, days_of_week: s.days_of_week, days_of_month: s.days_of_month, tier: s.tier, enabled: s.enabled } })}>
                    Edit
                  </button>
                  {" "}
                  <button className="danger" onClick={() => remove(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
